// src/pages/super-admin/Users/CreateClientUser.tsx
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCreateClientUserMutation } from "@/api/clientApi";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface CreateClientUserProps {
  clientId: number;
  onSuccess?: () => void;
}

export default function CreateClientUser({
  clientId,
  onSuccess,
}: CreateClientUserProps) {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
    role: "CLIENT_EMPLOYEE" as "CLIENT" | "CLIENT_EMPLOYEE",
  });

  const [createUser, { isLoading, error }] = useCreateClientUserMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.firstName || !form.lastName || !form.email || !form.password) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (form.password !== form.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    try {
      await createUser({ 
        clientId, 
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phoneNumber: form.phoneNumber || undefined,
        password: form.password,
        role: form.role,
      }).unwrap();
      
      toast.success("Employee created successfully with login credentials");
      setForm({ 
        firstName: "", 
        lastName: "", 
        email: "", 
        phoneNumber: "",
        password: "",
        confirmPassword: "",
        role: "CLIENT_EMPLOYEE",
      });
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to create employee");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="firstName">First Name *</Label>
          <Input
            id="firstName"
            placeholder="First Name"
            value={form.firstName}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
            required
            className="mt-1.5"
          />
        </div>
        <div>
          <Label htmlFor="lastName">Last Name *</Label>
          <Input
            id="lastName"
            placeholder="Last Name"
            value={form.lastName}
            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
            required
            className="mt-1.5"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="email">Email *</Label>
        <Input
          id="email"
          type="email"
          placeholder="employee@example.com"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          required
          className="mt-1.5"
        />
      </div>

      <div>
        <Label htmlFor="phoneNumber">Phone Number</Label>
        <Input
          id="phoneNumber"
          placeholder="+1234567890"
          value={form.phoneNumber}
          onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })}
          className="mt-1.5"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="password">Password *</Label>
          <Input
            id="password"
            type="password"
            placeholder="Minimum 6 characters"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required
            className="mt-1.5"
            minLength={6}
          />
        </div>
        <div>
          <Label htmlFor="confirmPassword">Confirm Password *</Label>
          <Input
            id="confirmPassword"
            type="password"
            placeholder="Confirm password"
            value={form.confirmPassword}
            onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
            required
            className="mt-1.5"
            minLength={6}
          />
        </div>
      </div>

      <div>
        <Label>Role *</Label>
        <Select 
          value={form.role} 
          onValueChange={(value: "CLIENT" | "CLIENT_EMPLOYEE") => setForm({ ...form, role: value })}
        >
          <SelectTrigger className="mt-1.5">
            <SelectValue placeholder="Select role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="CLIENT">Client Admin</SelectItem>
            <SelectItem value="CLIENT_EMPLOYEE">Client Employee</SelectItem>
          </SelectContent>
        </Select>
      </div>


      {error && (
        <p className="text-sm text-red-500">
          Error: {(error as any).data?.message || "Failed to create employee"}
        </p>
      )}

      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? "Creating..." : "Create Employee with Login"}
      </Button>
    </form>
  );
}
